import { useTranslation } from "react-i18next";
import { formatDuration } from "../../utils/eventFormat.js";
import { labelStyle, valueStyle } from "./eventSectionStyles.js";

/**
 * The strip under the hero: genre, format, duration, who runs it, and
 * whether registration is still open. Every column is optional — an event
 * that only knows two of them renders two, rather than printing empty
 * cells or the word "undefined".
 */
export default function EventFactBar({ event, lang }) {
  const { t } = useTranslation();
  const f = event;

  const organiser = f.externalOrga ? f.orgaName || t("events.externalOrgaBadge") : "Tales&Legends";
  // "Registration: closed" on an event that happened months ago is noise,
  // not information — the date badge in the hero already says it is over.
  const registration =
    f.status === "past"
      ? null
      : f.registrationDeadline
        ? t("events.factRegistrationUntil", { date: formatDeadline(f.registrationDeadline, lang) })
        : f.registrationOpen
          ? t("events.factRegistrationOpen")
          : t("events.factRegistrationClosed");

  const facts = [
    { key: "genre", label: t("events.factGenre"), value: f.genre },
    { key: "format", label: t("events.factFormat"), value: f.format },
    { key: "duration", label: t("events.factDuration"), value: formatDuration(f.duration, lang) },
    { key: "organiser", label: t("events.factOrganiser"), value: organiser },
    { key: "registration", label: t("events.factRegistration"), value: registration, highlight: true },
  ].filter((fact) => fact.value);

  if (facts.length === 0) return null;

  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(170px, 100%), 1fr))",
        border: "1px solid rgba(254,250,239,0.12)",
        borderRadius: "var(--radius-card)",
        overflow: "hidden",
        margin: 0,
      }}
    >
      {facts.map((fact, index) => (
        <div
          key={fact.key}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            padding: "22px 24px",
            borderLeft: index === 0 ? "none" : "1px solid rgba(254,250,239,0.12)",
          }}
        >
          <dt style={labelStyle}>{fact.label}</dt>
          <dd style={{ ...valueStyle, color: fact.highlight ? "var(--color-gold)" : valueStyle.color, margin: 0 }}>
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatDeadline(date, lang) {
  return new Date(date).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
  });
}
